// Per-function entries, never the `date-fns` root barrel, and per-locale,
// never `date-fns/locale`. Metro has no tree shaking: those two barrels put
// every date-fns function and all ~240 locales (Afrikaans through Vietnamese)
// into the web bundle — 1,076 KB for the five helpers this file actually uses.
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { differenceInHours } from 'date-fns/differenceInHours'
import { isToday as fnsIsToday } from 'date-fns/isToday'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { es } from 'date-fns/locale/es'

export const DEFAULT_BOOKING_TIME_ZONE = 'America/El_Salvador'
export const DEFAULT_BOOKING_UTC_OFFSET = '-06:00'

/**
 * Rental days between two instants, counted where the CAR is.
 *
 * This used to be `differenceInCalendarDays`, which counts civil days in the
 * DEVICE timezone. Every caller uses it for trip length — the booking quote, the
 * cancellation refund policy, the booking screen — so a guest whose phone is not
 * on SV time could see a trip one day shorter or longer than the one the server
 * prices, and create-payment's authoritative charge would answer 409 with no way
 * for them to proceed.
 *
 * A rental day is a day the car is unavailable in San Salvador, not a day on the
 * renter's phone. Counting in the booking timezone is both the correct meaning
 * and what makes client and server agree by construction rather than by the
 * coincidence of everyone being in the same country.
 *
 * Falls back to the device count if Intl has no timezone data, which is the same
 * answer this function always gave.
 */
export function daysBetween(start: Date, end: Date): number {
  const startParts = datePartsInTimeZone(start)
  const endParts = datePartsInTimeZone(end)
  if (!startParts || !endParts) return Math.abs(differenceInCalendarDays(end, start))

  const civilDayIndex = (p: { year: string; month: string; day: string }): number =>
    Math.floor(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)) / 86_400_000)

  return Math.abs(civilDayIndex(endParts) - civilDayIndex(startParts))
}

/**
 * Days a rental is BILLED for: the calendar span, never less than one.
 *
 * `daysBetween` answers a calendar question and returns 0 for a booking that
 * starts and ends on the same SV day. That is the right answer to the question
 * asked, and the wrong number to put in a price: the product allows a 4-hour
 * rental (RENTAL_LIMITS.minHours, and all 22 active vehicles use that default),
 * so same-day bookings are ordinary. The booking screen quoted them at zero days
 * and create-payment charged one — a guaranteed RENTAL_TOTAL_MISMATCH that no
 * guest could get past.
 *
 * The floor lives here, named once, instead of being a `Math.max(…, 1)` that two
 * of the four call sites remembered. deriveAuthoritativeCharge applies the same
 * floor server-side, and scripts/validate-pricing-day-parity.mjs runs both and
 * fails if they ever disagree.
 */
export function rentalDaysBetween(start: Date, end: Date): number {
  return Math.max(daysBetween(start, end), 1)
}

export function formatRelative(date: Date | string, locale: string = 'es'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return formatDistanceToNow(d, {
    addSuffix: true,
    locale: locale === 'es' ? es : undefined,
  })
}

export function isToday(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date
  return fnsIsToday(d)
}

export function isWithinHours(date: Date | string, hours: number): boolean {
  const d = typeof date === 'string' ? new Date(date) : date
  return Math.abs(differenceInHours(new Date(), d)) <= hours
}

export function toLocalDate(utcString: string): Date {
  return new Date(utcString)
}

/**
 * Today in the user's local timezone as ISO `YYYY-MM-DD`.
 * Centralised so every DatePicker across the app uses the same "today"
 * reference — screens were previously each rolling their own implementation.
 */
export function todayISO(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function datePartsInTimeZone(
  date: Date,
  timeZone: string = DEFAULT_BOOKING_TIME_ZONE,
): { year: string; month: string; day: string; hour: string; minute: string } | null {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    const parts = Object.fromEntries(
      formatter.formatToParts(date).map((part) => [part.type, part.value]),
    )
    if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute) return null
    return {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour,
      minute: parts.minute,
    }
  } catch {
    return null
  }
}

function parseBookingDateValue(value: string | Date): Date {
  if (typeof value !== 'string') return value
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T12:00:00${DEFAULT_BOOKING_UTC_OFFSET}`)
  }
  return new Date(value)
}

export function todayInBookingTimeZoneISO(timeZone: string = DEFAULT_BOOKING_TIME_ZONE): string {
  return bookingClockNow(new Date(), timeZone).date
}

/**
 * Civil clock where the car lives. Device TZ must not decide "today" or
 * "is this 10:00 already past" — a guest on UTC+2 would otherwise pick a
 * Salvadoran morning that already ended.
 */
export function bookingClockNow(
  now: Date = new Date(),
  timeZone: string = DEFAULT_BOOKING_TIME_ZONE,
): { date: string; time: string } {
  const parts = datePartsInTimeZone(now, timeZone)
  if (!parts) {
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hour = String(now.getHours()).padStart(2, '0')
    const minute = String(now.getMinutes()).padStart(2, '0')
    return { date: `${y}-${m}-${day}`, time: `${hour}:${minute}` }
  }
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  }
}

export function isBookingDateTimeInThePast(
  date: string,
  time: string,
  now: Date = new Date(),
): boolean {
  const start = new Date(toBookingDateTimeISO(date, time)).getTime()
  return Number.isFinite(start) && start < now.getTime()
}

export function toBookingDateTimeISO(date: string, time: string): string {
  return `${date}T${time}:00${DEFAULT_BOOKING_UTC_OFFSET}`
}

export function bookingDateKey(value: string | Date | null | undefined): string | null {
  if (!value) return null
  const date = parseBookingDateValue(value)
  if (Number.isNaN(date.getTime())) return null
  const parts = datePartsInTimeZone(date)
  if (!parts) return null
  return `${parts.year}-${parts.month}-${parts.day}`
}

export function bookingTimeHHmm(value: string | Date | null | undefined): string | null {
  if (!value) return null
  const date = parseBookingDateValue(value)
  if (Number.isNaN(date.getTime())) return null
  const parts = datePartsInTimeZone(date)
  if (!parts) return null
  return `${parts.hour}:${parts.minute}`
}

export function formatBookingDate(
  value: string | Date | null | undefined,
  locale: string = 'es',
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return '-'
  const date = parseBookingDateValue(value)
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '-'
  return new Intl.DateTimeFormat(locale === 'es' ? 'es-SV' : 'en-US', {
    timeZone: DEFAULT_BOOKING_TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options,
  }).format(date)
}

export function formatBookingDateTime(
  value: string | Date | null | undefined,
  locale: string = 'es',
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return '-'
  const date = parseBookingDateValue(value)
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '-'
  return new Intl.DateTimeFormat(locale === 'es' ? 'es-SV' : 'en-US', {
    timeZone: DEFAULT_BOOKING_TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }).format(date)
}

/**
 * Month + year pinned to the booking timezone — review dates, "member since".
 * Same TZ/locale contract as formatBookingDate, without the day component.
 */
export function formatBookingMonthYear(
  value: string | Date | null | undefined,
  locale: string = 'es',
  month: 'short' | 'long' = 'long',
): string {
  if (!value) return '-'
  const date = parseBookingDateValue(value)
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '-'
  return new Intl.DateTimeFormat(locale === 'es' ? 'es-SV' : 'en-US', {
    timeZone: DEFAULT_BOOKING_TIME_ZONE,
    month,
    year: 'numeric',
  }).format(date)
}

/**
 * User-facing Salvadoran date format for form fields.
 * Stored values stay ISO `YYYY-MM-DD`; display stays familiar `DD/MM/YYYY`.
 */
export function formatSvDateDisplay(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return iso
  return `${match[3]}/${match[2]}/${match[1]}`
}

/**
 * User-facing Salvadoran month/year format for documents that only print VTO MM-YYYY.
 */
export function formatSvMonthYearDisplay(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-\d{2}$/)
  if (!match) return iso
  return `${match[2]}/${match[1]}`
}

/**
 * Calendar-valid ISO `YYYY-MM-DD`, or null.
 * Day `00` (OCR of a month-year VTO) becomes the last day of that month —
 * Postgres rejects `2031-03-00` (TAGO-MOBILE-3G).
 */
export function validIsoDate(value: string | null | undefined): string | null {
  if (!value) return null
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (day === 0 && month >= 1 && month <= 12) {
    return lastDayOfMonthISO(year, month)
  }
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }
  return `${match[1]}-${match[2]}-${match[3]}`
}

/**
 * ISO date for the last calendar day of a month.
 * Month is 1-based.
 */
export function lastDayOfMonthISO(year: number, month: number): string {
  const date = new Date(year, month, 0)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * ISO `YYYY-MM-DD` for the date exactly `yearsBack` years before today.
 * Used for age-gate min/max (e.g. 18-year DOB cutoff on edit-profile).
 */
export function yearsAgoISO(yearsBack: number): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - yearsBack)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * ISO `YYYY-MM-DD` for a date `daysAhead` days in the future (or past if negative).
 */
export function daysFromTodayISO(daysAhead: number): string {
  const d = new Date()
  d.setDate(d.getDate() + daysAhead)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Calendar-exact age in whole years between `dob` and `now`.
 * Single source of truth for every age check (signup, profile, KYC).
 */
export function yearsBetween(dob: Date, now: Date): number {
  let age = now.getFullYear() - dob.getFullYear()
  const m = now.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1
  return age
}

/**
 * ¿Dos marcas de tiempo son el mismo instante? Comparado como TIEMPO, no texto.
 *
 * POR QUÉ EXISTE. Postgres devuelve un `timestamptz` como
 * '2026-08-29 20:30:00+00' y el teléfono manda '2026-08-29T20:30:00.000Z'. Son
 * el mismo momento escrito de dos formas, así que compararlos con `===` NUNCA
 * da verdadero.
 *
 * Eso rompió el corto-circuito de "esta foto ya está arriba" en la subida de
 * inspecciones: la condición jamás se cumplía y un reintento por UNA foto
 * perdida volvía a comprimir, hashear y subir las dieciséis — varios megas de
 * datos móviles, mientras la pantalla prometía justo lo contrario.
 */
export function esElMismoInstante(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false
  const ta = new Date(a).getTime()
  const tb = new Date(b).getTime()
  return Number.isFinite(ta) && Number.isFinite(tb) && ta === tb
}
